import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Star, Users, BookOpen, MessageCircle, Upload, Calendar } from 'lucide-react';

const EnrollPage = () => {
  const navigate = useNavigate();
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [loading, setLoading] = useState(false);

  const mentorshipTracks = [
    {
      id: 'apostolic_prophetic_track',
      name: 'Apostolic–Prophetic Leadership Track',
      price: 100,
      description: 'Deep dive into apostolic and prophetic ministry, developing leaders who move in both the governing and revelatory dimensions of the Kingdom.',
      benefits: [
        'Full Community access',
        'Complete Dashboard with progress tracking',
        'Academy Journey tracker',
        'File upload capabilities',
        'All 71 ebooks FREE',
        '2 FREE courses of your choice',
        '1 monthly Q&A session with mentor',
        'Certificate upon completion'
      ]
    },
    {
      id: 'minstrel_worship_track',
      name: 'Minstrel & Worship Leadership Track',
      price: 100,
      description: 'Train in the art of worship, prophetic worship, and leading others into the presence of God through music and ministry.',
      benefits: [
        'Full Community access',
        'Complete Dashboard with progress tracking',
        'Academy Journey tracker',
        'File upload capabilities',
        'All 71 ebooks FREE',
        '2 FREE courses of your choice',
        '1 monthly Q&A session with mentor',
        'Certificate upon completion'
      ]
    }
  ];

  const handleEnroll = async (trackId) => {
    // Check if user is logged in before attempting payment
    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('Please log in to enroll in a mentorship track.');
      window.location.href = '/login?redirect=/enroll';
      return;
    }
    
    console.log('Enrollment started for track:', trackId);
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const originUrl = window.location.origin;
      
      console.log('Calling payment API:', `${backendUrl}/api/payments/checkout`);
      console.log('Package ID:', trackId);
      
      const response = await fetch(`${backendUrl}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          package_id: trackId,
          origin_url: originUrl
        })
      });

      console.log('Payment response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Payment session created, redirecting to:', data.url);
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else if (response.status === 401 || response.status === 403) {
        // Session expired - clear token and redirect to login
        localStorage.removeItem('access_token');
        alert('Your session has expired. Please log in again to continue.');
        window.location.href = '/login?redirect=/enroll';
      } else {
        const error = await response.json();
        console.error('Payment error:', error);
        alert(error.detail || 'Failed to create payment session. Please try again.');
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <Star className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 text-purple-400" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-4 sm:mb-6 px-4">Enroll in Academy</h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            Choose your academy track and begin your transformational journey. Flexible payment options available.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="mt-6 bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-xl font-semibold transition-colors"
          >
            View All Payment Plans
          </button>
        </div>
      </div>

      {/* Academy Tracks */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          {mentorshipTracks.map((track) => (
            <div
              key={track.id}
              className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all ${
                selectedTrack === track.id ? 'ring-4 ring-purple-500' : ''
              }`}
            >
              <div className="p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
                  {track.name}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{track.description}</p>
                
                <div className="flex items-baseline mb-4 sm:mb-6">
                  <span className="text-3xl sm:text-4xl font-bold">From £120</span>
                  <span className="text-sm sm:text-base text-gray-500 ml-2">per month</span>
                </div>
                <p className="text-sm text-green-600 font-medium mb-4">or £1,200 upfront (Save £200)</p>

                <div className="space-y-3 mb-8">
                  {track.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleEnroll(track.id)}
                  disabled={loading}
                  className="w-full bg-black text-white py-4 rounded-xl text-lg font-medium hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : `Enroll in ${track.name.split(' ')[0]} Track`}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* What's Included Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-sm p-8">
          <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">
            What's Included in Academy Membership
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Full Community Access</h3>
              <p className="text-gray-600">Connect with fellow mentees, share insights, and grow together in our vibrant community.</p>
            </div>

            <div className="text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">71 Free Ebooks</h3>
              <p className="text-gray-600">Access our entire library of spiritual resources, leadership materials, and ministry guides.</p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Unlimited Appointments</h3>
              <p className="text-gray-600">Book as many Q&A sessions as you need with your mentor via Zoom.</p>
            </div>
          </div>
        </div>

        {/* Guest vs Member Comparison */}
        <div className="mt-16">
          <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">
            Guest vs Academy Member
          </h2>
          
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Guest (Free)</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Member (From £120/mo)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Community Access</td>
                  <td className="px-6 py-4 text-center text-red-500">✗</td>
                  <td className="px-6 py-4 text-center text-green-500">✓</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Ebooks</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">£5-£10 each</td>
                  <td className="px-6 py-4 text-center text-green-500">All FREE</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Courses</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">£300 each</td>
                  <td className="px-6 py-4 text-center text-green-500">2 FREE + more</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Q&A Sessions</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">1 session only</td>
                  <td className="px-6 py-4 text-center text-green-500">Unlimited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">File Uploads</td>
                  <td className="px-6 py-4 text-center text-red-500">✗</td>
                  <td className="px-6 py-4 text-center text-green-500">✓</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-900">Progress Tracking</td>
                  <td className="px-6 py-4 text-center text-red-500">✗</td>
                  <td className="px-6 py-4 text-center text-green-500">✓</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrollPage;
